import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { AcademyButton, DateInput, compactInputBase } from '../controls';
import { addDays, daysBetween, todayKST } from '../periodDates';
import { SegTabs } from '../ui';
import { FlowContent, HeadcountContent, WithdrawContent } from './ReportContent';
import ScrollRegion from './ScrollRegion';
import { shiftMonth, validDate } from './reportModel';
import AdmissionsReport from './AdmissionsReport';
import NotesReport from './NotesReport';

export const REPORT_TITLES = {
  headcount: '재원 통계', flows: '입원·퇴원 추이', groups: '반·강사 통계', withdrawals: '퇴원 예정 학생', notes: '학원·반 메모', admissions: '입학률',
};

function DateRangeForm({ from, to, bucket, withBucket, onApply, rangeError }) {
  const [draft, setDraft] = useState({ from, to, bucket });
  const error = !validDate(draft.from) || !validDate(draft.to) ? '시작일과 종료일을 입력해 주세요.'
    : draft.from > draft.to ? '종료일은 시작일과 같거나 뒤여야 합니다.'
      : daysBetween(draft.from, draft.to) + 1 > (withBucket ? { day: 400, week: 1096, month: 3653 }[draft.bucket] : 400) ? '조회 기간이 너무 깁니다. 기간을 줄여 주세요.' : null;
  return (
    <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (!error) onApply(draft); }}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1 text-xs text-gray-500"><span className="block">시작일</span><DateInput aria-label="시작일" compact fullWidth={false} value={draft.from} onChange={(value) => setDraft({ ...draft, from: value })} className="w-36" /></label>
        <label className="space-y-1 text-xs text-gray-500"><span className="block">종료일</span><DateInput aria-label="종료일" compact fullWidth={false} value={draft.to} onChange={(value) => setDraft({ ...draft, to: value })} className="w-36" /></label>
        {withBucket && <label className="space-y-1 text-xs text-gray-500"><span className="block">집계 단위</span><select aria-label="집계 단위" value={draft.bucket} onChange={(e) => setDraft({ ...draft, bucket: e.target.value })} className={compactInputBase}><option value="day">일별</option><option value="week">주별</option><option value="month">월별</option></select></label>}
        <AcademyButton type="submit" disabled={!!error}>조회</AcademyButton>
      </div>
      {(error || rangeError) && <p role="alert" className="text-xs text-danger">{error || rangeError}</p>}
    </form>
  );
}

export default function ReportPage({ report, filters, onChange, onBack, onOpenStudent, scrollKey }) {
  const today = todayKST();
  const scheduled = report === 'withdrawals';
  const from = filters.from ?? (scheduled ? today : `${shiftMonth(today.slice(0, 7), -5)}-01`);
  const to = filters.to ?? (scheduled ? addDays(today, 29) : today);
  const bucket = ['day', 'week', 'month'].includes(filters.bucket) ? filters.bucket : 'month';
  const groupBy = filters.group === 'teacher' ? 'teacher' : 'class';
  const asOf = validDate(filters.as_of) && filters.as_of <= today ? filters.as_of : today;
  const by = filters.by === 'school' ? 'school' : 'grade';
  const withRange = ['flows', 'groups', 'withdrawals'].includes(report);
  const rangeError = !validDate(from) || !validDate(to) || from > to ? '조회 날짜를 확인해 주세요.'
    : daysBetween(from, to) + 1 > (scheduled ? 400 : { day: 400, week: 1096, month: 3653 }[bucket]) ? '조회 기간이 너무 깁니다. 기간이나 집계 단위를 바꿔 주세요.' : null;

  return (
    <div className="flex size-full min-w-0 flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      <div className="flex shrink-0 items-center gap-2 border-b px-4 py-3">
        <AcademyButton aria-label="학원 현황으로" onClick={onBack}><ArrowLeft className="size-4" /></AcademyButton>
        <h3 className="min-w-0 text-base font-semibold text-gray-900">{REPORT_TITLES[report]}</h3>
      </div>
      <ScrollRegion scrollKey={scrollKey}>
        {report === 'admissions' && <AdmissionsReport filters={filters} onChange={onChange} onOpenStudent={onOpenStudent} />}
        {withRange && <DateRangeForm key={`${from}-${to}-${bucket}`} from={from} to={to} bucket={bucket} withBucket={!scheduled} rangeError={rangeError} onApply={({ from: start, to: end, bucket: unit }) => onChange({ from: start, to: end, bucket: scheduled ? null : unit })} />}
        {report === 'headcount' && <>
          <div className="flex flex-wrap items-center gap-2">
            <SegTabs tabs={[["grade", '학년별'], ['school', '학교별']]} value={by} onChange={(value) => onChange({ by: value })} ariaLabel="재원 통계 구분" />
            <label className="ml-auto flex items-center gap-2 text-xs text-gray-500">기준일<DateInput aria-label="기준일" key={asOf} value={asOf} max={today} compact fullWidth={false} onChange={(value) => { if (validDate(value) && value <= today) onChange({ as_of: value }); }} /></label>
          </div>
          <HeadcountContent asOf={asOf} by={by} schoolLevel={filters.school_level} onSchoolLevelChange={(value) => onChange({ school_level: value })} />
        </>}
        {report === 'groups' && <SegTabs tabs={[["class", '반별'], ['teacher', '강사별']]} value={groupBy} onChange={(value) => onChange({ group: value })} ariaLabel="통계 대상" />}
        {!rangeError && ['flows', 'groups'].includes(report) && <FlowContent from={from} to={to} bucket={bucket} groupBy={report === 'groups' ? groupBy : 'none'} />}
        {!rangeError && scheduled && <WithdrawContent from={from} to={to} onOpenStudent={onOpenStudent} />}
        {report === 'notes' && <NotesReport filters={filters} onChange={onChange} />}
      </ScrollRegion>
    </div>
  );
}
